import React, {useCallback, useEffect, useState, useMemo} from 'react'
import {useLocation} from 'react-router-dom'
import {usePage} from '@salesforce/commerce-sdk-react'
import {PageContext, Component} from '@salesforce/commerce-sdk-react/components'
import {NewsletterSubscriptionModalContextProvider} from '../../custom-contexts'

import {
    Box,
    Modal,
    ModalBody,
    ModalOverlay,
    ModalContent,
    ModalCloseButton,
    useDisclosure
} from '@salesforce/retail-react-app/app/components/shared/ui'

import {PDCNewsletterSubscription} from '../../page-designer/assets'
import {
    getLocalStorageJSONItem,
    setLocalStorageJSONItem,
    getPageDesignerRegion
} from '../../utils/utils'

const PAGEDESIGNER_TO_COMPONENT = {
    'commerce_assets.pdcNewsletterSubscription': PDCNewsletterSubscription
}

import {NEWSLETTER_MODAL_OPEN_TIME, NEWSLETTER_SUBSCRIPTION_MODAL_PAGE_ID} from '../../constants'

/**
 * NewsletterSubscriptionModal component responsible for managing the display and behavior
 * of a newsletter subscription modal dialog. This component utilizes the Chakra UI modal
 * system and integrates with page data and local storage to determine when the modal should
 * be displayed to the user.
 *
 * The modal will open based on the following conditions:
 * 1. The user has not previously interacted with the modal, as stored in local storage.
 * 2. The page data is available, which is set up via Page Designer and Promotion on SFCC.
 * 3. No other modals are currently open on the page.
 *
 * The component uses a timeout to delay the modal opening logic, allowing for any necessary
 * asynchronous operations to complete.
 *
 * The modal can be closed by invoking the onCloseHandler, which sets a local storage item
 * to indicate that the modal has been used and calls the onClose callback.
 *
 * @component
 * @returns {JSX.Element} The rendered newsletter subscription modal component.
 */
export const NewsletterSubscriptionModal = () => {
    const {isOpen, onOpen, onClose} = useDisclosure()
    const [shouldFetchData, setShouldFetchData] = useState(false)

    const location = useLocation()

    const {data: pageData} = usePage(
        {
            parameters: {pageId: NEWSLETTER_SUBSCRIPTION_MODAL_PAGE_ID}
        },
        {
            enabled: shouldFetchData
        }
    )
    const [contextValue] = useState({components: PAGEDESIGNER_TO_COMPONENT})

    const mainRegion = getPageDesignerRegion(pageData, 'main')
    const mainRegionComponents = mainRegion?.components

    /**
     * Handles the logic for opening the newsletter subscription modal.
     * The modal will open if the following conditions are met:
     * 1. The user has not previously interacted with the modal, as determined by local storage.
     * 2. The page data is available, which is set up via PD and Campaign on SFCC.
     * 3. No other modals are currently open.
     *
     * The function uses a timeout to delay the execution, allowing for any necessary asynchronous
     * operations to complete before checking the conditions.
     */
    const handleModalOpen = () => {
        setTimeout(() => {
            // Checks if we have data in local storage regarding to this modal already being used
            const userAlreadyUsedSubscriptionModal = getLocalStorageJSONItem('newsletterModalUsed')

            if (userAlreadyUsedSubscriptionModal) {
                return false
            }

            // Check if there is other modal open
            const isAnyModalOpen =
                document?.querySelectorAll('.chakra-modal__content-container')?.length > 0

            if (isAnyModalOpen) {
                return false
            }

            setShouldFetchData(true)
        }, NEWSLETTER_MODAL_OPEN_TIME)
    }

    useEffect(() => {
        handleModalOpen()
        // We pass location also as a dependency, because we might be in the case where the modal is not opening
        // Because other modal is opened already
        // But we need to try again when user navigates to a different page
    }, [location])

    useEffect(() => {
        if (shouldFetchData && pageData) {
            onOpen()
        }
    }, [pageData, shouldFetchData])

    /**
     * Handles the closing of the newsletter subscription modal.
     * Sets a local storage item to indicate that the modal has been used,
     * and then invokes the provided onClose callback function.
     *
     * @function
     * @returns {void}
     */
    const onCloseHandler = useCallback(() => {
        setLocalStorageJSONItem('newsletterModalUsed', true)
        onClose()
    }, [])

    const memoizedValue = useMemo(() => ({onClose}), [onClose])

    return (
        <NewsletterSubscriptionModalContextProvider value={memoizedValue}>
            <Modal isOpen={isOpen} onClose={onCloseHandler} size={'large'}>
                <ModalOverlay />
                <ModalContent maxWidth="790px" padding="0">
                    <ModalCloseButton />
                    <ModalBody p="0">
                        {mainRegionComponents?.length > 0 &&
                            mainRegionComponents.map((component) => (
                                <Box key={component.id}>
                                    <PageContext.Provider value={contextValue}>
                                        <Component component={component} />
                                    </PageContext.Provider>
                                </Box>
                            ))}
                    </ModalBody>
                </ModalContent>
            </Modal>
        </NewsletterSubscriptionModalContextProvider>
    )
}
